"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiStarFill, PiShoppingCart, PiArrowRightBold } from "react-icons/pi";
import logger from "@/utils/logger";

interface Product {
  title: string;
  category: string;
  price: string;
  sales: string;
  rating: number;
  ratingCount?: string;
  image: string;
  url?: string;
  ctaText?: string;
  showCta?: boolean;
}

interface Tab {
  label: string;
  filter: string;
}

export default function HokProducts({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const [activeTab, setActiveTab] = useState(content.activeTab || 0);

  const tabs: Tab[] = content.tabs || [
    { label: "All Courses", filter: "all" },
    { label: "Development", filter: "site" },
    { label: "Design", filter: "mobile" },
    { label: "Business", filter: "present" },
    { label: "Marketing", filter: "ecom" },
    { label: "IT & Software", filter: "landing" },
    { label: "Personal Growth", filter: "dashboard" },
  ];

  const defaultProducts: Product[] = [
    {
      title: "Complete Web Development Bootcamp",
      category: "Development",
      price: "$39",
      sales: "1200 Students",
      rating: 5,
      ratingCount: "123",
      image: "/assets/img/product/product_2_1.png",
      url: "course-details.html",
      ctaText: "Enroll Now",
      showCta: true,
    },
    {
      title: "UI/UX Design Masterclass - Zero to Hero",
      category: "Design",
      price: "$49",
      sales: "1500 Students",
      rating: 5,
      ratingCount: "123",
      image: "/assets/img/product/product_2_2.png",
      url: "course-details.html",
      ctaText: "Enroll Now",
      showCta: true,
    },
    {
      title: "Digital Marketing Strategy & Implementation",
      category: "Marketing",
      price: "$59",
      sales: "800 Students",
      rating: 5,
      ratingCount: "123",
      image: "/assets/img/product/product_2_3.png",
      url: "course-details.html",
      ctaText: "Enroll Now",
      showCta: true,
    },
    {
      title: "Python for Data Science - Complete Guide",
      category: "Data Science",
      price: "$29",
      sales: "2000 Students",
      rating: 5,
      ratingCount: "123",
      image: "/assets/img/product/product_2_4.png",
      url: "course-details.html",
      ctaText: "Enroll Now",
      showCta: true,
    },
  ];

  const products: Product[] = content.products || defaultProducts;

  const backgroundImage = content.backgroundImage || "/assets/img/bg/product_bg_1.png";

  const updateTab = (idx: number) => {
    if (!onContentChange) return;
    setActiveTab(idx);
    onContentChange({ ...content, activeTab: idx });
  };

  const updateProduct = (idx: number, field: keyof Product, value: any) => {
    if (!onContentChange) return;
    const newProducts = [...products];
    newProducts[idx] = { ...newProducts[idx], [field]: value };
    onContentChange({ ...content, products: newProducts });
  };

  const addProduct = () => {
    if (!onContentChange) return;
    const newProducts = [...products, {
      title: "New Product",
      category: "Category",
      price: "$49",
      sales: "0 Sales",
      rating: 5,
      ratingCount: "0",
      image: "/assets/img/product/product_2_1.png",
      url: "shop-details.html",
      ctaText: "Enroll Now",
      showCta: true,
    }];
    onContentChange({ ...content, products: newProducts });
  };

  const removeProduct = (idx: number) => {
    if (!onContentChange) return;
    const newProducts = products.filter((_, i) => i !== idx);
    onContentChange({ ...content, products: newProducts });
  };

  return (
    <section
      className={`relative overflow-hidden w-full py-16 sm:py-20 ${isSelected ? "ring-2 ring-blue-500 relative z-20" : ""}`}
      style={{
        fontFamily,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        {/* Section header */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <div className="text-center max-w-2xl">
            <span
              className="inline-block text-xs sm:text-sm font-bold tracking-wider uppercase mb-3"
              style={{ color: primaryColor }}
            >
              <EditableText
                value={content.subtitle || "Featured Courses"}
                onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                as="span"
                placeholder="Subtitle"
              />
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
              <EditableText
                value={content.title || "Newest Courses & Tutorials"}
                onChange={(val) => onContentChange?.({ ...content, title: val })}
                as="span"
                placeholder="Title"
              />
            </h2>
            <p className="text-gray-600 text-base max-w-xl mx-auto">
              <EditableText
                value={content.description || "Each week, our team curates the best courses to help you learn new skills and advance your career."}
                onChange={(val) => onContentChange?.({ ...content, description: val })}
                as="span"
                placeholder="Description"
              />
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
          {tabs.map((tab, idx) => (
            <button
              key={idx}
              onClick={() => updateTab(idx)}
              className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all ${activeTab === idx
                  ? "text-white shadow-md"
                  : "text-gray-600 bg-white hover:bg-gray-50 border border-gray-200"
                }`}
              style={activeTab === idx ? { background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` } : {}}
            >
              <EditableText
                value={tab.label}
                onChange={(val) => {
                  if (!onContentChange) return;
                  const newTabs = [...tabs];
                  newTabs[idx] = { ...newTabs[idx], label: val };
                  onContentChange({ ...content, tabs: newTabs });
                }}
                as="span"
                placeholder="Tab Label"
              />
            </button>
          ))}
        </div>

        {/* Products grid - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {products.map((product, idx) => (
            <div
              key={idx}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {isSelected && (
                <button
                  onClick={() => removeProduct(idx)}
                  className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold"
                  title="Remove Product"
                >
                  ×
                </button>
              )}

              {/* Product image */}
              <div className="relative overflow-hidden group/img">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {isSelected && (
                  <label className="absolute bottom-3 right-3 z-10 flex items-center justify-center p-2 bg-white/95 backdrop-blur-xs border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-all shadow-sm hover:scale-105">
                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const toast = (await import("react-hot-toast")).default;
                          const homepageApi = (await import("@/services/homepageApi")).default;
                          const toastId = toast.loading("Uploading image...");
                          try {
                            const response = await homepageApi.uploadImage(file);
                            const url = response.data?.url || response.url;
                            if (url) {
                              updateProduct(idx, "image", url);
                              toast.success("Image uploaded successfully!", { id: toastId });
                            } else {
                              toast.error("Failed to get uploaded path.", { id: toastId });
                            }
                          } catch (err) {
                            toast.error("Upload failed", { id: toastId });
                            logger.error(err);
                          }
                        }
                      }}
                    />
                  </label>
                )}
                {/* Price tag */}
                <span
                  className="absolute top-3 left-3 px-3 py-1 rounded-lg text-sm font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                >
                  <EditableText
                    value={product.price}
                    onChange={(val) => updateProduct(idx, "price", val)}
                    as="span"
                    placeholder="$0"
                  />
                </span>

                {/* Hover actions overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <a
                    href={product.url || "#"}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    View Details
                    <PiArrowRightBold className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Product content */}
              <div className="p-5">
                <span
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: primaryColor }}
                >
                  <EditableText
                    value={product.category}
                    onChange={(val) => updateProduct(idx, "category", val)}
                    as="span"
                    placeholder="Category"
                  />
                </span>

                <h3 className="text-base font-bold text-gray-900 mt-1 mb-4 leading-snug line-clamp-2">
                  <EditableText
                    value={product.title}
                    onChange={(val) => updateProduct(idx, "title", val)}
                    as="span"
                    placeholder="Product Title"
                  />
                </h3>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      <EditableText
                        value={product.sales}
                        onChange={(val) => updateProduct(idx, "sales", val)}
                        as="span"
                        placeholder="0 Sales"
                      />
                    </p>
                    <div className="flex items-center gap-1 text-amber-400">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <PiStarFill key={star} className="w-3 h-3" />
                      ))}
                      <span className="text-xs text-gray-400 ml-1">({product.ratingCount || product.rating})</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <button
                        onClick={() => {
                          const currentVal = product.showCta !== false;
                          updateProduct(idx, "showCta", !currentVal);
                        }}
                        className={`p-1.5 rounded-lg border transition-colors ${product.showCta !== false
                            ? "bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100"
                            : "bg-gray-100 border-gray-200 text-gray-400 hover:bg-gray-200"
                          }`}
                        title={product.showCta !== false ? "Hide CTA Button" : "Show CTA Button"}
                      >
                        {product.showCta !== false ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                          </svg>
                        )}
                      </button>
                    )}
                    {product.showCta !== false ? (
                      <a
                        href={product.url || "#"}
                        onClick={(e) => {
                          if (onContentChange) e.preventDefault();
                        }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white shadow-xs hover:shadow-md transition-all whitespace-nowrap"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                      >
                        <EditableText
                          value={product.ctaText || "Enroll Now"}
                          onChange={(val) => updateProduct(idx, "ctaText", val)}
                          as="span"
                          placeholder="CTA Text"
                        />
                        <PiArrowRightBold className="w-3 h-3" />
                      </a>
                    ) : (
                      isSelected && (
                        <span className="text-xs text-gray-400 italic font-medium">Hidden</span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isSelected && (
            <button
              onClick={addProduct}
              className="flex flex-col items-center justify-center min-h-[320px] rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all"
            >
              <span className="text-4xl mb-2">+</span>
              <span className="text-sm font-semibold">Add Product</span>
            </button>
          )}
        </div>

        {/* Bottom CTA */}
        {content.showCta !== false && (
          <div className="flex justify-center mt-12 sm:mt-16">
            <a
              href={content.ctaUrl || "#"}
              onClick={(e) => {
                if (onContentChange) {
                  e.preventDefault();
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg text-sm font-bold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
            >
              <EditableText
                value={content.ctaText || "Explore All Courses"}
                onChange={(val) => onContentChange?.({ ...content, ctaText: val })}
                as="span"
                placeholder="CTA Text"
              />
              <PiArrowRightBold className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
